import { CSSProperties, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { mainButton } from '@telegram-apps/sdk-react';

import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';
import { AlcoholPreference } from '@/enums/apiEnums.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import { helpers } from '@/helpers.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import {
  iconCircleStyle,
  innerCircleStyle,
  svgContainerStyle,
  titleStyle
} from '@/pages/Profile/Edit/styles.ts';
import {
  containerStyle,
  inputContainerStyle,
  listItemStyle,
  scrollableListStyle,
  selectedItemMarkStyle,
  selectedItemStyle,
  unselectedItemStyle
} from '@/pages/Profile/Edit/EditEmployment/styles.ts';
import AlcoholShape from '@/components/common/svg/shapes/AlcoholShape.tsx';

export const getAlcoholPreferences = (
  t: TFunction
): Record<AlcoholPreference, string> => ({
  [AlcoholPreference.ALCOHOL_PREFERENCE_UNSPECIFIED]: t('status_unspecified'),
  [AlcoholPreference.ALCOHOL_PREFERENCE_OFTEN_ENOUGH]: t(
    'alcohol_preference.often_enough'
  ),
  [AlcoholPreference.ALCOHOL_PREFERENCE_SOCIALLY]: t(
    'alcohol_preference.socially'
  ),
  [AlcoholPreference.ALCOHOL_PREFERENCE_DONT_DRINK_ALCOHOL]: t(
    'alcohol_preference.dont_drink'
  )
});

interface EditAlcoholPreferencePopupProps {
  handleClose: () => void;
}

const EditAlcoholPreferencePopup: FC<EditAlcoholPreferencePopupProps> = ({
  handleClose
}) => {
  useBackButtonToCloseModal(handleClose, false);

  const { aboutMe, updateProfile } = useProfileStore();
  const { t } = useTranslation();

  const alcoholPreferences = getAlcoholPreferences(t);

  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [selectedPreference, setSelectedPreference] =
    useState<AlcoholPreference>(
      aboutMe?.profile.alcohol_preference ??
        AlcoholPreference.ALCOHOL_PREFERENCE_UNSPECIFIED
    );

  const selectAlcoholPreference = (value: AlcoholPreference) => {
    setIsEdited(true);
    setSelectedPreference(value);
  };

  const submitAlcoholPreference = async () => {
    mainButton.setParams({ isLoaderVisible: true, isEnabled: false });

    try {
      await updateProfile({ alcohol_preference: selectedPreference });
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_SUCCESS,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_SUCCESS} />
      });
      handleClose();
    } catch {
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_ERROR,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_ERROR} />
      });
    } finally {
      mainButton.setParams({
        isLoaderVisible: false,
        isVisible: false,
        isEnabled: true
      });
    }
  };

  useEffect(() => {
    mainButton.setParams({
      isVisible: isEdited
    });
  }, [isEdited]);

  useEffect(() => {
    mainButton.setParams({
      isEnabled: true,
      backgroundColor: '#000000',
      text: t('button.save')
    });

    mainButton.onClick(submitAlcoholPreference);
    return () => mainButton.offClick(submitAlcoholPreference);
  }, [selectedPreference]);

  return (
    <div style={popupContainerStyle}>
      <div style={svgContainerStyle}>
        <div style={iconCircleStyle}>
          <div style={innerCircleStyle}>
            <AlcoholShape width={25} height={25} fillColor="#fff" />
          </div>
        </div>
        <div style={titleStyle}>{t('indicate_alcohol')}</div>
      </div>
      <div style={inputContainerStyle}>
        <InputContent>
          <div style={containerStyle}>
            <div style={scrollableListStyle}>
              {Object.entries(alcoholPreferences).map(([value, label]) => (
                <div
                  key={value}
                  onClick={() =>
                    selectAlcoholPreference(value as AlcoholPreference)
                  }
                  style={{
                    ...listItemStyle,
                    ...(selectedPreference === (value as AlcoholPreference)
                      ? selectedItemStyle
                      : unselectedItemStyle),
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  {label}
                  {selectedPreference === (value as AlcoholPreference) && (
                    <span style={selectedItemMarkStyle}>✓</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </InputContent>
      </div>
    </div>
  );
};

const popupContainerStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'auto'
};

export default EditAlcoholPreferencePopup;
